import type { ReactElement } from "react";
import { Button, Stack } from "../ui-kit";
import type { MetricGranularity } from "./fixtures";

export interface PeriodSelectorProps {
  value: MetricGranularity;
  onChange: (value: MetricGranularity) => void;
  disabled?: boolean;
  busy?: boolean;
  /** Overrides the default group label — set when the page has more than one selector. */
  "aria-label"?: string;
}

const OPTIONS: readonly { value: MetricGranularity; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "month", label: "Month" },
];

/**
 * Day/month toggle driving which fixture slice the T3 growth charts render
 * (ISSUE-352 rescoping). Composed from the UI Kit `Button` as a toggle-button
 * group (APG "Button" pattern — `role="group"` + per-button `aria-pressed`)
 * rather than a bespoke design-system primitive; every option stays a native
 * `<button>` so keyboard and screen-reader support come from the platform,
 * not custom key handling. `md` size (44px) throughout — this control appears
 * on the 375px viewport and is never the sole primary action on the page, but
 * still needs a full touch target.
 */
export function PeriodSelector({
  value,
  onChange,
  disabled = false,
  busy = false,
  "aria-label": ariaLabel = "Period granularity",
}: PeriodSelectorProps): ReactElement {
  return (
    <Stack direction="row" gap={2} role="group" aria-label={ariaLabel} aria-busy={busy || undefined}>
      {OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? "primary" : "secondary"}
          aria-pressed={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </Stack>
  );
}
